<template>
	<div>
		<!-- Header -->
		<div class="page-header d-flex align-items-center justify-content-between my-4">
			<div>
				<h2 class="page-title">Auto Campaign Dashboard</h2>
				<p class="page-subtitle">Monitor and track your automated campaign performance</p>
			</div>
			<router-link :to="{ name: 'autoCampaignSettings' }" class="settings-btn">
				<i class="fad fa-cog btn-icon"></i>
				<span class="btn-tooltip">Settings</span>
			</router-link>
		</div>

		<!-- Action Cards -->
		<div class="row mb-4">
			<div class="col-md-3 mb-3">
				<div class="action-card">
					<div class="card-body">
						<div class="card-icon-wrapper">
							<div class="card-icon">
								<i class="fad fa-plus"></i>
							</div>
						</div>
						<div class="card-content">
							<h5 class="card-title">Create new campaign</h5>
							<p class="card-description">Set up automated letters, postcards, and share them with municipalities.</p>
						</div>
						<div class="card-arrow">
							<router-link :to="{ name: 'createAutoCampaign' }">
								<i class="fad fa-chevron-right"></i>
							</router-link>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-3 mb-3">
				<div class="action-card data-lists-card">
					<div class="card-body">
						<div class="card-icon-wrapper">
							<div class="card-icon">
								<i class="fad fa-database"></i>
							</div>
						</div>
						<div class="card-content">
							<h5 class="card-title">Campaign Data Lists</h5>
							<p class="card-description">Manage and organize your campaign contact lists.</p>
						</div>
						<div class="card-arrow">
							<a href="#" @click.prevent="navigateToDataLists">
								<i class="fad fa-chevron-right"></i>
							</a>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-3 mb-3">
				<div class="action-card orange">
					<div class="card-body">
						<div class="card-icon-wrapper">
							<div class="card-icon">
								<i class="fad fa-list"></i>
							</div>
						</div>
						<div class="card-content">
							<h5 class="card-title">View all campaigns</h5>
							<p class="card-description">Browse and manage all your automated campaigns in one place.</p>
						</div>
						<div class="card-arrow">
							<router-link :to="{ name: 'autoCampaigns' }">
								<i class="fad fa-chevron-right"></i>
							</router-link>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-3 mb-3">
				<div class="action-card purple">
					<div class="card-body">
						<div class="card-icon-wrapper">
							<div class="card-icon">
								<i class="fad fa-clock"></i>
							</div>
						</div>
						<div class="card-content">
							<h5 class="card-title">Last run campaign</h5>
							<div v-if="lastRunCampaign">
								<p class="card-description">{{ lastRunCampaign.auto_campaign.name }} - {{ formatLastRunDate(lastRunCampaign.run_date) }}</p>
								<div class="campaign-badges">
									<span class="badge badge-blue" :title="'Total sent: ' + lastRunCampaign.total_sent">{{ lastRunCampaign.total_sent }}</span>
									<span class="badge badge-green" :title="'Delivered: ' + lastRunCampaign.delivered">{{ lastRunCampaign.delivered }}</span>
									<span class="badge badge-red" :title="'Errors: ' + lastRunCampaign.errors">{{ lastRunCampaign.errors }}</span>
								</div>
							</div>
							<div v-else>
								<p class="card-description text-muted">No finished runs yet</p>
							</div>
						</div>
						<div class="card-arrow" v-if="lastRunCampaign">
							<router-link :to="{ name: 'autoCampaignRunReport', params: { runId: lastRunCampaign.id } }">
								<i class="fad fa-chevron-right"></i>
							</router-link>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Campaigns Overview -->
		<div class="row mb-4">
			<div class="col-md-6">
				<div class="action-card green">
					<div class="card-body">
						<div class="section-header d-flex justify-content-between align-items-center mb-3">
							<div class="d-flex align-items-center">
								<div class="card-icon me-3">
									<i class="fad fa-play-circle"></i>
								</div>
								<h5 class="section-title mb-0">Next Runs</h5>
							</div>
							<router-link :to="{ name: 'autoCampaigns' }" class="btn btn-sm btn-outline-primary d-none">
								View All
							</router-link>
						</div>
						<div class="campaigns-list">
							<div class="campaign-item" v-for="nextRun in displayedNextRuns" :key="nextRun.id">
								<div class="campaign-row">
									<div class="campaign-details">
										<h6 class="campaign-name">
											<router-link :to="{ name: 'autoCampaignRuns', params: { id: nextRun.auto_campaign.id } }" class="campaign-name-link">
												{{ nextRun.auto_campaign.name }}
											</router-link>
											<span class="campaign-type-badge ms-2">{{ nextRun.auto_campaign.type }}</span>
										</h6>
										<small class="campaign-recipients">{{ nextRun.total_items }} recipients</small>
									</div>
									<div class="campaign-image-column">
										<!-- Postcard type -->
										<div v-if="nextRun.auto_campaign.type === 'postcard'" class="campaign-postcard-flip">
											<div class="campaign-flip-inner">
												<div class="campaign-flip-front">
													<img :src="getPostcardUrl(nextRun.auto_campaign.postcard_front)" alt="Front" class="campaign-postcard-thumb" />
												</div>
												<div class="campaign-flip-back">
													<img :src="getPostcardUrl(nextRun.auto_campaign.postcard_back)" alt="Back" class="campaign-postcard-thumb" />
												</div>
											</div>
										</div>
										<!-- Letter type -->
										<div v-else class="campaign-file-icon">
											<div class="file-icon-container">
												<i class="fad fa-file-alt fa-2x"></i>
											</div>
										</div>
									</div>
									<div class="campaign-created">
										<span class="code-label">Created:</span>
										<span class="created-value">{{ formatRelativeTime(nextRun.created_at) }}</span>
									</div>
									<div class="campaign-code">
										<span class="code-label">Run ID:</span>
										<span class="code-value">{{ nextRun.code }}</span>
									</div>
									<div class="campaign-actions">
										<!-- Show "Start Campaign" button when status is prepared -->
										<button v-if="nextRun.status === 'prepared'" class="btn btn-sm btn-primary" @click="startCampaignRun(nextRun)" title="Start Campaign Run">
											<i class="fad fa-truck-fast fa-2x"></i>
										</button>

										<!-- Show "Cancel" button when status is sending -->
										<button v-if="nextRun.status === 'sending'" class="btn btn-sm btn-danger" @click="cancelCampaignRun(nextRun)" title="Cancel Campaign">
											<i class="fad fa-times-circle fa-2x"></i>
										</button>
									</div>
								</div>
							</div>
							<div class="text-center mt-3" v-if="!showAllCampaigns && nextRuns.length > 3">
								<button class="btn btn-outline-secondary btn-sm" @click="toggleShowAll">Show More ({{ nextRuns.length - 3 }} more)</button>
							</div>
							<div class="text-center mt-3" v-if="showAllCampaigns && nextRuns.length > 3">
								<button class="btn btn-outline-secondary btn-sm" @click="toggleShowAll">
									Show Less
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="col-md-6">
				<div class="action-card">
					<div class="card-body">
						<div class="section-header d-flex justify-content-between align-items-center mb-3">
							<div class="d-flex align-items-center">
								<div class="card-icon me-3">
									<i class="fad fa-file-alt"></i>
								</div>
								<h5 class="section-title mb-0">Mail Delivery Statistics</h5>
							</div>
							<select class="form-select time-period-select" v-model="selectedTimePeriod" @change="updateChartData">
								<option value="week">This Week</option>
								<option value="month">This Month</option>
								<option value="quarter">This Quarter</option>
							</select>
						</div>
						<div class="chart-widget">
							<div class="chart-container">
								<canvas ref="deliveryChart" width="400" height="200"></canvas>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Developer tools -->
		<section class="dev-tools mb-5">
			<div class="dev-tools__header">
				<div>
					<div class="dev-tools__eyebrow"><i class="fad fa-code me-2"></i>Developer tools</div>
					<h3>Fire an auto-campaign trigger</h3>
					<p>Choose a trigger and the incoming municipality. Works without a hook — it fires the active campaigns attached to that trigger. Test is a dry run; Run creates prepared campaign runs.</p>
				</div>
				<span class="dev-tools__badge">DEV</span>
			</div>

			<div class="dev-tools__body">
				<div class="dev-field">
					<label>Trigger</label>
					<select v-model="devTriggerId" class="form-select" @change="devHookResult = null">
						<option :value="null">Select a trigger…</option>
						<option v-for="trigger in devTriggers" :key="trigger.id" :value="trigger.id">{{ trigger.name }} — {{ trigger.hook || 'no hook' }} ({{ trigger.active_campaigns.length }} active)</option>
					</select>
					<small v-if="selectedDevTrigger">
						{{ selectedDevTrigger.active_campaigns.map(c => c.name).join(', ') || 'No active campaigns on this trigger.' }}
						<template v-if="selectedDevTrigger.hook && !selectedDevTrigger.hook_wired"> · Hook not wired in mc2-api — only manual runs will fire it.</template>
					</small>
				</div>

				<div class="dev-field dev-municipality-field">
					<label>Triggering municipality</label>
					<input v-model="devMunicipalitySearch" class="form-control" type="text" placeholder="Search by municipality name or name, state…" @input="searchDevMunicipalities" />
					<div v-if="devMunicipalitySearching" class="dev-search-note">Searching…</div>
					<div v-if="devMunicipalities.length" class="dev-municipality-results">
						<button v-for="municipality in devMunicipalities" :key="municipality.id" type="button" @click="selectDevMunicipality(municipality)">
							<strong>{{ municipality.full_name }}, {{ municipality.state }}</strong>
							<span>{{ municipality.county }} County · Population {{ municipality.population || 'unknown' }} · ID {{ municipality.id }}</span>
						</button>
					</div>
					<div v-if="devMunicipality" class="dev-selected"><i class="fad fa-check-circle me-2"></i>{{ devMunicipality.full_name }}, {{ devMunicipality.state }} <span>ID {{ devMunicipality.id }}</span></div>
				</div>

				<div class="dev-actions">
					<button type="button" class="dev-btn" :disabled="!canRunDevHook || devHookBusy" @click="runDevHook(true)"><i class="fad me-2" :class="devHookBusy === 'test' ? 'fa-spinner fa-spin' : 'fa-vial'"></i>Test trigger</button>
					<button type="button" class="dev-btn dev-btn--run" :disabled="!canRunDevHook || devHookBusy" @click="runDevHook(false)"><i class="fad me-2" :class="devHookBusy === 'run' ? 'fa-spinner fa-spin' : 'fa-bolt'"></i>Run trigger</button>
				</div>

				<div v-if="devHookResult" class="dev-result" :class="devHookResult.ok ? 'dev-result--ok' : 'dev-result--error'">
					<strong>{{ devHookResult.message }}</strong>
					<div v-if="devHookResult.campaigns">Would trigger: {{ devHookResult.campaigns.map(c => c.name).join(', ') || 'no active campaigns' }}</div>
					<div v-if="devHookResult.runs">Runs: {{ devHookResult.runs.map(r => `${r.campaign_name} → ${r.run_code || 'failed'}`).join(', ') || 'none' }}</div>
				</div>
			</div>
		</section>

		<ConfirmModal
			ref="runDevHookModal"
			title="Run auto-campaign trigger?"
			message="This creates real prepared runs for every active campaign attached to the selected trigger."
			confirm-text="Run trigger"
			confirm-icon="fa-bolt"
			icon-class="fa-bolt"
			:busy="devHookBusy === 'run'"
			@confirm="confirmRunDevHook"
		/>
	</div>
</template>

<script>
import Chart from 'chart.js/auto'
import restAPI from '@/api/index.js'
import { mapState } from 'vuex'
import ConfirmModal from '@/components/common/ConfirmModal.vue'

export default {
	name: 'AutoCampaignHome',
	components: { ConfirmModal },
	data() {
		return {
			lastRunCampaign: null,
			nextRuns: [],
			selectedTimePeriod: 'week',
			showAllCampaigns: false,
			chartData: [
				{ date: 'Mon', total: 45, delivered: 42, errors: 3 },
				{ date: 'Tue', total: 78, delivered: 74, errors: 4 },
				{ date: 'Wed', total: 123, delivered: 118, errors: 5 },
				{ date: 'Thu', total: 89, delivered: 84, errors: 5 },
				{ date: 'Fri', total: 156, delivered: 149, errors: 7 },
				{ date: 'Sat', total: 67, delivered: 63, errors: 4 },
				{ date: 'Sun', total: 34, delivered: 32, errors: 2 },
			],
			chart: null,
			devTriggers: [],
			devTriggerId: null,
			devMunicipalitySearch: '',
			devMunicipalities: [],
			devMunicipality: null,
			devMunicipalitySearching: false,
			devHookBusy: null,
			devHookResult: null,
			devSearchTimer: null,
		}
	},
	computed: {
		...mapState(['storageUrl']),
		displayedNextRuns() {
			return this.showAllCampaigns ? this.nextRuns : this.nextRuns.slice(0, 3)
		},
		totalSent() {
			return this.chartData.reduce((sum, item) => sum + item.total, 0)
		},
		avgPerDay() {
			return Math.round(this.totalSent / this.chartData.length)
		},
		peakDay() {
			const peak = this.chartData.reduce((max, item) => (item.total > max.total ? item : max), this.chartData[0])
			return peak ? peak.date : 'N/A'
		},
		selectedDevTrigger() {
			return this.devTriggers.find(trigger => trigger.id === this.devTriggerId) || null
		},
		canRunDevHook() {
			return !!(this.devTriggerId && this.devMunicipality)
		},
	},
	async mounted() {
		this.initializeChart()
		await this.fetchNextRuns()
		await this.fetchLastRunCampaign()
		await this.fetchDevHooks()
	},
	beforeDestroy() {
		this.destroyChart()
		if (this.devSearchTimer) clearTimeout(this.devSearchTimer)
	},
	methods: {
		async fetchDevHooks() {
			try {
				const { data } = await restAPI.get('/auto-campaign-hooks')
				this.devTriggers = data?.triggers || []
			} catch (error) {
				console.error('Error fetching developer hooks:', error)
			}
		},
		searchDevMunicipalities() {
			this.devMunicipality = null
			this.devHookResult = null
			if (this.devSearchTimer) clearTimeout(this.devSearchTimer)
			const search = this.devMunicipalitySearch.trim()
			if (search.length < 2) {
				this.devMunicipalities = []
				return
			}
			this.devSearchTimer = setTimeout(async () => {
				this.devMunicipalitySearching = true
				try {
					const { data } = await restAPI.get('/auto-campaign-hooks/municipality-search', { params: { search } })
					this.devMunicipalities = data?.municipalities || []
				} catch (error) {
					this.devMunicipalities = []
				} finally {
					this.devMunicipalitySearching = false
				}
			}, 300)
		},
		selectDevMunicipality(municipality) {
			this.devMunicipality = municipality
			this.devMunicipalitySearch = `${municipality.full_name}, ${municipality.state}`
			this.devMunicipalities = []
		},
		async runDevHook(test) {
			if (!this.canRunDevHook || this.devHookBusy) return
			if (!test) {
				this.$refs.runDevHookModal.open()
				return
			}
			await this.executeDevHook(true)
		},
		async confirmRunDevHook() {
			await this.executeDevHook(false)
		},
		async executeDevHook(test) {
			if (!this.canRunDevHook || this.devHookBusy) return
			this.devHookBusy = test ? 'test' : 'run'
			this.devHookResult = null
			try {
				const { data } = await restAPI.post('/auto-campaign-hooks/manual-run', { trigger_id: this.devTriggerId, municipality_id: this.devMunicipality.id, test })
				this.devHookResult = { ok: true, message: data.message, campaigns: data.campaigns, runs: data.runs }
				if (!test) {
					await this.fetchNextRuns()
					this.$refs.runDevHookModal.close()
				}
			} catch (error) {
				this.devHookResult = { ok: false, message: error?.response?.data?.message || 'Hook execution failed' }
			} finally {
				this.devHookBusy = null
			}
		},
		getPostcardUrl(postcardPath) {
			return postcardPath ? this.storageUrl + '/' + postcardPath : null
		},
		formatLastRunDate(dateString) {
			const date = new Date(dateString)
			const now = new Date()
			const diffTime = Math.abs(now - date)
			const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

			if (diffDays === 1) return 'Yesterday'
			if (diffDays === 0) return 'Today'
			return `${diffDays} days ago`
		},
		formatRelativeTime(dateString) {
			const date = new Date(dateString)
			const now = new Date()
			const diffTime = Math.abs(now - date)

			const diffSeconds = Math.floor(diffTime / 1000)
			const diffMinutes = Math.floor(diffSeconds / 60)
			const diffHours = Math.floor(diffMinutes / 60)
			const diffDays = Math.floor(diffHours / 24)

			if (diffSeconds < 60) return 'Just now'
			if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
			if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`
			if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`
			if (diffDays < 30) {
				const weeks = Math.floor(diffDays / 7)
				return `${weeks} week${weeks !== 1 ? 's' : ''} ago`
			}
			if (diffDays < 365) {
				const months = Math.floor(diffDays / 30)
				return `${months} month${months !== 1 ? 's' : ''} ago`
			}
			const years = Math.floor(diffDays / 365)
			return `${years} year${years !== 1 ? 's' : ''} ago`
		},
		formatScheduledDate(dateString) {
			const date = new Date(dateString)
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		},
		getStatusClass(status) {
			const classes = {
				prepared: 'status-prepared',
				running: 'status-running',
				completed: 'status-completed',
				failed: 'status-failed',
				cancelled: 'status-cancelled',
				scheduled: 'status-scheduled',
			}
			return classes[status] || 'status-default'
		},
		calculateEstimatedCost(campaign) {
			if (!campaign || !campaign.total_items || !campaign.auto_campaign) return '0.00'
			const costPerItem = campaign.auto_campaign.type === 'postcard' ? 0.75 : 0.65
			return (campaign.total_items * costPerItem).toFixed(2)
		},
		getScheduledText(campaign) {
			if (!campaign || !campaign.scheduled_at) return 'Not scheduled'
			return this.formatScheduledDate(campaign.scheduled_at)
		},
		async sendCampaignRun(campaign) {
			try {
				// Show loading state
				const loadingToast = this.$swal.fire({
					title: 'Sending Campaign...',
					text: `Preparing to send ${campaign.auto_campaign.name}`,
					allowOutsideClick: false,
					didOpen: () => {
						this.$swal.showLoading()
					},
				})

				// Call the API endpoint
				const response = await restAPI.post('/auto-campaign-lob/send-campaign', {
					campaign_run_id: campaign.id,
					campaign_id: campaign.auto_campaign.id,
				})

				// Close loading toast
				loadingToast.close()

				// Handle different response scenarios
				if (response.status === 200 || response.status === 201) {
					const responseData = response.data

					// Show success message with response details
					let successText = `${campaign.auto_campaign.name} has been queued for processing.`

					if (responseData.message) {
						successText = responseData.message
					}

					if (responseData.job_id) {
						successText += ` Job ID: ${responseData.job_id}`
					}

					if (responseData.estimated_completion) {
						successText += ` Estimated completion: ${responseData.estimated_completion}`
					}

					this.$swal.fire({
						icon: 'success',
						title: 'Campaign Sent Successfully!',
						text: successText,
						confirmButtonText: 'Got it',
						timer: 7000,
						timerProgressBar: true,
					})

					// Log success for debugging
					console.log('Campaign sent successfully:', responseData)
				} else {
					// Handle unexpected status codes
					throw new Error(`Unexpected response status: ${response.status}`)
				}

				// Refresh the data to update the UI
				await this.fetchNextRuns()
				await this.fetchLastRunCampaign()
			} catch (error) {
				console.error('Error sending campaign:', error)

				// Close any loading toast
				this.$swal.close()

				let errorTitle = 'Failed to Send Campaign'
				let errorMessage = 'An unexpected error occurred while sending the campaign.'
				let showRetry = true

				// Handle different types of errors
				if (error.response) {
					// Server responded with error status
					const status = error.response.status
					const responseData = error.response.data

					switch (status) {
						case 400:
							errorTitle = 'Invalid Request'
							errorMessage = responseData.message || 'The campaign request is invalid. Please check the campaign data.'
							break
						case 401:
							errorTitle = 'Authentication Required'
							errorMessage = 'You need to log in again to send campaigns.'
							showRetry = false
							break
						case 403:
							errorTitle = 'Access Denied'
							errorMessage = "You don't have permission to send this campaign."
							showRetry = false
							break
						case 404:
							errorTitle = 'Campaign Not Found'
							errorMessage = 'The campaign or campaign run could not be found.'
							showRetry = false
							break
						case 422:
							errorTitle = 'Validation Error'
							errorMessage = responseData.message || 'Campaign data validation failed.'
							if (responseData.errors) {
								const errors = Object.values(responseData.errors).flat()
								errorMessage += ` Details: ${errors.join(', ')}`
							}
							break
						case 429:
							errorTitle = 'Rate Limited'
							errorMessage = 'Too many requests. Please wait a moment before trying again.'
							break
						case 500:
							errorTitle = 'Server Error'
							errorMessage = responseData.message || 'A server error occurred. Please try again later.'
							break
						default:
							errorMessage = responseData.message || `Server error (${status}). Please try again.`
					}
				} else if (error.request) {
					// Network error
					errorTitle = 'Network Error'
					errorMessage = 'Unable to connect to the server. Please check your internet connection.'
				} else {
					// Something else happened
					errorMessage = error.message || errorMessage
				}

				// Show error dialog with optional retry
				const alertConfig = {
					icon: 'error',
					title: errorTitle,
					text: errorMessage,
					confirmButtonText: showRetry ? 'Try Again' : 'Close',
					showCancelButton: showRetry,
					cancelButtonText: 'Cancel',
				}

				this.$swal.fire(alertConfig).then(result => {
					// If user clicks "Try Again", retry the operation
					if (result.isConfirmed && showRetry) {
						this.sendCampaignRun(campaign)
					}
				})
			}
		},
		startCampaignRun(campaign) {
			// Navigate to the campaign run start view
			this.$router.push({
				name: 'autoCampaignRunStart',
				params: {
					runId: campaign.id,
				},
			})
		},
		async cancelCampaignRun(campaign) {
			try {
				// Show confirmation dialog
				const result = await this.$swal.fire({
					title: 'Cancel Campaign?',
					text: `Are you sure you want to cancel "${campaign.auto_campaign.name}"? This action cannot be undone.`,
					icon: 'warning',
					showCancelButton: true,
					confirmButtonColor: '#ef4444',
					cancelButtonColor: '#6b7280',
					confirmButtonText: 'Yes, Cancel Campaign',
					cancelButtonText: 'Keep Running',
				})

				if (result.isConfirmed) {
					// Show loading state
					const loadingToast = this.$swal.fire({
						title: 'Cancelling Campaign...',
						text: 'Please wait while we stop the campaign',
						allowOutsideClick: false,
						didOpen: () => {
							this.$swal.showLoading()
						},
					})

					// Call cancel API endpoint
					await restAPI.post('/auto-campaign-runs/cancel', {
						campaign_run_id: campaign.id,
					})

					loadingToast.close()

					// Show success message
					this.$swal.fire({
						icon: 'success',
						title: 'Campaign Cancelled',
						text: `${campaign.auto_campaign.name} has been cancelled successfully.`,
						timer: 5000,
						timerProgressBar: true,
					})

					// Refresh data
					await this.fetchNextRuns()
					await this.fetchLastRunCampaign()
				}
			} catch (error) {
				console.error('Error cancelling campaign:', error)
				this.$swal.close()

				this.$swal.fire({
					icon: 'error',
					title: 'Failed to Cancel Campaign',
					text: error.response?.data?.message || 'An error occurred while cancelling the campaign.',
					confirmButtonText: 'Close',
				})
			}
		},
		updateChartData() {
			if (this.chart) {
				this.chart.destroy()
			}
			this.initializeChart()
		},
		initializeChart() {
			const ctx = this.$refs.deliveryChart.getContext('2d')
			this.chart = new Chart(ctx, {
				type: 'line',
				data: {
					labels: this.chartData.map(item => item.date),
					datasets: [
						{
							label: 'Total Sent',
							data: this.chartData.map(item => item.total),
							borderColor: '#3b82f6',
							backgroundColor: 'rgba(59, 130, 246, 0.1)',
							borderWidth: 2,
							fill: false,
							tension: 0.4,
							pointBackgroundColor: '#3b82f6',
							pointBorderColor: '#ffffff',
							pointBorderWidth: 2,
							pointRadius: 4,
							pointHoverRadius: 6,
						},
						{
							label: 'Successfully Delivered',
							data: this.chartData.map(item => item.delivered),
							borderColor: '#10b981',
							backgroundColor: 'rgba(16, 185, 129, 0.1)',
							borderWidth: 2,
							fill: false,
							tension: 0.4,
							pointBackgroundColor: '#10b981',
							pointBorderColor: '#ffffff',
							pointBorderWidth: 2,
							pointRadius: 4,
							pointHoverRadius: 6,
						},
						{
							label: 'Errors',
							data: this.chartData.map(item => item.errors),
							borderColor: '#ef4444',
							backgroundColor: 'rgba(239, 68, 68, 0.1)',
							borderWidth: 2,
							fill: false,
							tension: 0.4,
							pointBackgroundColor: '#ef4444',
							pointBorderColor: '#ffffff',
							pointBorderWidth: 2,
							pointRadius: 4,
							pointHoverRadius: 6,
						},
					],
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					scales: {
						x: {
							beginAtZero: true,
							grid: {
								display: false,
							},
							ticks: {
								font: {
									size: 12,
								},
							},
						},
						y: {
							beginAtZero: true,
							grid: {
								color: '#f3f4f6',
								borderDash: [5, 5],
							},
							ticks: {
								font: {
									size: 12,
								},
							},
						},
					},
					plugins: {
						legend: {
							display: true,
							position: 'top',
							labels: {
								boxWidth: 12,
								font: {
									size: 12,
								},
								padding: 20,
								usePointStyle: true,
							},
						},
						tooltip: {
							callbacks: {
								label: function(context) {
									let label = context.dataset.label || ''
									if (label) {
										label += ': '
									}
									if (context.parsed.y !== null) {
										label += context.parsed.y
									}
									return label
								},
							},
						},
					},
				},
			})
		},
		destroyChart() {
			if (this.chart) {
				this.chart.destroy()
				this.chart = null
			}
		},
		toggleShowAll() {
			this.showAllCampaigns = !this.showAllCampaigns
		},
		async fetchNextRuns() {
			try {
				const response = await restAPI.get('/auto-campaign-runs/prepared')
				this.nextRuns = response.data
			} catch (error) {
				console.error('Error fetching next runs:', error)
				this.nextRuns = []
			}
		},
		async fetchLastRunCampaign() {
			try {
				const response = await restAPI.get('/auto-campaign-runs/last-finished')
				this.lastRunCampaign = response.data
			} catch (error) {
				console.error('Error fetching last run campaign:', error)
				this.lastRunCampaign = null
			}
		},
		// Campaign Data Lists navigation method
		navigateToDataLists() {
			this.$router.push({ name: 'autoCampaignsDataLists' })
		},
	},
}
</script>

<style scoped>
/* Header Styles */
.page-header {
	padding: 0;
}

.page-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin: 0 0 4px 0;
	line-height: 1.2;
}

.page-subtitle {
	font-size: 14px;
	color: #6b7280;
	margin: 0;
	line-height: 1.4;
}

/* Settings Button - Styled like create campaign button */
.settings-btn {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #ffffff;
	border: 1px solid #e0e1e1;
	border-radius: 50%;
	color: #64748b;
	text-decoration: none;
	transition: all 0.2s ease;
	flex-shrink: 0;
	position: relative;
}

.settings-btn:hover {
	background: #f8f9fa;
	border-color: #d1d5db;
	color: #475569;
	text-decoration: none;
}

.settings-btn .btn-icon {
	transition: transform 0.3s ease;
}

.settings-btn:hover .btn-icon {
	transform: rotate(180deg);
}

.btn-tooltip {
	position: absolute;
	right: 50px;
	top: 50%;
	transform: translateY(-50%);
	background: #374151;
	color: #ffffff;
	padding: 6px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	opacity: 0;
	visibility: hidden;
	transition: all 0.2s ease;
	pointer-events: none;
	z-index: 1000;
}

.btn-tooltip::before {
	content: '';
	position: absolute;
	right: -5px;
	top: 50%;
	transform: translateY(-50%);
	border: 5px solid transparent;
	border-left-color: #374151;
}

.settings-btn:hover .btn-tooltip {
	opacity: 1;
	visibility: visible;
}

.back-button {
	padding: 0.5rem;
	color: #6b7280;
	border: none;
	background: none;
	font-size: 1.25rem;
	transition: color 0.3s ease;
}

.back-button:hover {
	color: #374151;
	background: none;
}

.user-avatar {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	background-color: #374151;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.2rem;
}

/* Action Cards */
.action-card {
	border-radius: 14px;
	background: linear-gradient(180deg, var(--Blue-50, #ebf1ff) 0%, var(--Surfaces-surface, #fff) 50%);
	padding: 0;
	height: 100%;
	transition: all 0.3s ease;
	position: relative;
}

.action-card.orange {
	background: linear-gradient(180deg, #fed7aa 0%, var(--Surfaces-surface, #fff) 50%);
}

.action-card.purple {
	background: linear-gradient(180deg, #e9d5ff 0%, var(--Surfaces-surface, #fff) 50%);
}

.action-card.green {
	background: linear-gradient(180deg, #bbf7d0 0%, var(--Surfaces-surface, #fff) 50%);
}

.action-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.action-card .card-body {
	padding: 24px 24px 16px 24px;
	height: 100%;
	display: flex;
	flex-direction: column;
	position: relative;
}

.card-icon-wrapper {
	margin-bottom: 20px;
}

.card-icon {
	width: 48px;
	height: 48px;
	border-radius: 12px;
	background-color: #6366f1;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 20px;
}

.action-card.orange .card-icon {
	background-color: #f97316;
}

.action-card.purple .card-icon {
	background-color: #8b5cf6;
}

.action-card.green .card-icon {
	background-color: #10b981;
}

.card-content {
	flex: 1;
	margin-bottom: 20px;
}

.card-title {
	font-size: 20px;
	font-weight: 600;
	color: #111827;
	margin-bottom: 8px;
	line-height: 1.3;
}

.card-description {
	color: #6b7280;
	font-size: 14px;
	line-height: 1.5;
	margin: 0;
}

.card-arrow {
	position: absolute;
	top: 20px;
	right: 20px;
}

.card-arrow a {
	color: #9ca3af;
	font-size: 18px;
	text-decoration: none;
	transition: color 0.3s ease;
}

.card-arrow a:hover {
	color: #6b7280;
}

/* Campaign Stats */
.campaign-badges {
	display: flex;
	gap: 8px;
	margin-top: 12px;
}

.badge {
	padding: 4px 8px;
	border-radius: 12px;
	font-size: 12px;
	font-weight: 600;
	color: white;
	cursor: default;
}

.badge-blue {
	background-color: #3b82f6;
}

.badge-green {
	background-color: #10b981;
}

.badge-red {
	background-color: #ef4444;
}

/* Summary Card */
.summary-card {
	background: #f0f9ff;
	border: 1px solid #bae6fd;
	border-radius: 12px;
	padding: 1.5rem;
	height: 100%;
}

.summary-icon {
	width: 48px;
	height: 48px;
	border-radius: 8px;
	background-color: #0ea5e9;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 1.5rem;
	margin-bottom: 1rem;
}

.summary-label {
	font-size: 0.875rem;
	font-weight: 600;
	color: #374151;
	margin-bottom: 0.5rem;
}

.summary-stat {
	color: #6b7280;
	font-size: 0.95rem;
	line-height: 1.5;
}

.summary-highlight {
	color: #0ea5e9;
	font-weight: 600;
}

/* Campaigns Section */
.campaigns-list {
	margin-top: 0;
}

.campaign-item {
	padding: 1rem 0;
	border-bottom: 1px solid #f1f5f9;
}

.campaign-item:last-child {
	border-bottom: none;
}

.campaign-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
}

.campaign-details {
	flex: 1;
	min-width: 0;
}

.campaign-image-column {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 80px;
}

.campaign-name {
	font-size: 1rem;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 0.25rem;
}

.campaign-name-link {
	color: #1f2937;
	text-decoration: none;
	transition: color 0.3s ease;
}

.campaign-name-link:hover {
	color: #3b82f6;
	text-decoration: underline;
}

.campaign-recipients {
	color: #6b7280;
	font-size: 0.875rem;
}

.campaign-created {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	min-width: 90px;
}

.campaign-code {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	min-width: 80px;
}

.created-value {
	font-size: 0.75rem;
	color: #059669;
	font-weight: 600;
	line-height: 1.2;
}

.code-label {
	font-size: 0.75rem;
	color: #6b7280;
	font-weight: 500;
	margin-bottom: 0.25rem;
}

.code-value {
	font-size: 0.65rem;
	color: #1f2937;
	font-weight: 600;
	font-family: 'Courier New', monospace;
}

.campaign-actions {
	display: flex;
	gap: 0.5rem;
	align-items: center;
}

.section-header {
	margin-bottom: 1.5rem;
}

.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 0.5rem;
}

.section-subtitle {
	color: #6b7280;
	font-size: 0.9rem;
	margin: 0;
}

/* Chart Section */
.chart-section {
	background: #f8fafc;
	border: 1px solid #e2e8f0;
	border-radius: 12px;
	padding: 1.5rem;
}

.section-header {
	margin-bottom: 1.5rem;
}

.section-title {
	font-size: 1.25rem;
	font-weight: 600;
	color: #1f2937;
	margin-bottom: 0.5rem;
}

.chart-header-icon {
	color: #6b7280;
	font-size: 1.1rem;
}

.time-period-select {
	width: auto;
	min-width: 120px;
	font-size: 0.875rem;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 0.5rem;
	background-color: white;
}

.time-period-select:focus {
	border-color: #3b82f6;
	box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.1);
	outline: none;
}

.chart-widget {
	margin-top: 0;
}

.chart-container {
	height: 280px;
	position: relative;
	margin-bottom: 1.5rem;
	border-radius: 8px;
	padding: 1rem;
}

.chart-summary {
	border-top: 1px solid #e5e7eb;
	padding-top: 1rem;
	background: white;
	border-radius: 8px;
	padding: 1rem;
}

.summary-stats {
	display: flex;
	justify-content: space-around;
	gap: 1rem;
}

.stat-item {
	text-align: center;
	flex: 1;
}

.stat-value {
	display: block;
	font-size: 1.1rem;
	font-weight: 700;
	color: #111827;
	margin-bottom: 0.25rem;
}

.stat-label {
	font-size: 0.75rem;
	color: #6b7280;
	font-weight: 500;
}

/* General Styles */
.card {
	border: none;
	border-radius: 12px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.btn {
	border-radius: 8px;
	font-weight: 500;
	transition: all 0.3s ease;
}

.btn-outline-secondary {
	border-color: #e2e8f0;
	color: #6b7280;
}

.btn-outline-secondary:hover {
	background-color: #f8fafc;
	border-color: #d1d5db;
}

.btn-outline-primary {
	border-color: #0ea5e9;
	color: #0ea5e9;
}

.btn-outline-primary:hover {
	background-color: #0ea5e9;
	border-color: #0ea5e9;
}

.btn-xs {
	padding: 0.25rem 0.5rem;
	font-size: 0.75rem;
	line-height: 1;
}

/* Postcard Flip Animation */
.postcard-flip-container {
	background-color: transparent;
	width: 450px;
	height: 375px;
	perspective: 1000px;
}

.postcard-flip-inner {
	position: relative;
	width: 100%;
	height: 100%;
	text-align: center;
	transition: transform 0.6s;
	transform-style: preserve-3d;
	cursor: pointer;
}

.postcard-flip-container:hover .postcard-flip-inner {
	transform: rotateY(180deg);
}

.postcard-flip-front,
.postcard-flip-back {
	position: absolute;
	width: 100%;
	height: 100%;
	-webkit-backface-visibility: hidden;
	backface-visibility: hidden;
}

.postcard-flip-front {
	background-color: transparent;
}

.postcard-flip-back {
	background-color: transparent;
	transform: rotateY(180deg);
}

.postcard-flip-front img,
.postcard-flip-back img {
	width: 450px;
	height: 315px;
	object-fit: cover;
}

.postcard-flip-front small,
.postcard-flip-back small {
	color: #6b7280;
	font-weight: 500;
}

/* Campaign Table Flip Animation */
.campaign-postcard-flip {
	background-color: transparent;
	width: 62px;
	height: 44px;
	perspective: 1000px;
}

.campaign-flip-inner {
	position: relative;
	width: 100%;
	height: 100%;
	text-align: center;
	transition: transform 0.4s;
	transform-style: preserve-3d;
	cursor: pointer;
}

.campaign-postcard-flip:hover .campaign-flip-inner {
	transform: rotateY(180deg);
}

.campaign-flip-front,
.campaign-flip-back {
	position: absolute;
	width: 100%;
	height: 100%;
	-webkit-backface-visibility: hidden;
	backface-visibility: hidden;
}

.campaign-flip-front {
	background-color: transparent;
}

.campaign-flip-back {
	background-color: transparent;
	transform: rotateY(180deg);
}

.campaign-postcard-thumb {
	width: 62px;
	height: 44px;
	object-fit: cover;
	border-radius: 4px;
	border: 1px solid #e5e7eb;
}

/* File Icon Styles */
.campaign-file-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 62px;
	height: 44px;
}

.file-icon {
	font-size: 18px;
	margin-bottom: 2px;
	color: white;
}

.file-type-label {
	font-size: 8px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #e5e7eb;
	line-height: 1;
}

/* Campaign Type Label in Modal */
.campaign-type-label {
	display: inline-block;
	padding: 4px 12px;
	font-size: 12px;
	font-weight: 600;
	text-transform: capitalize;
	border-radius: 20px;
	background-color: #f3f4f6;
	color: #374151;
	border: 1px solid #e5e7eb;
}

/* Campaign Type Badge in Table */
.campaign-type-badge {
	display: inline-block;
	padding: 2px 8px;
	font-size: 10px;
	font-weight: 600;
	text-transform: capitalize;
	border-radius: 12px;
	background-color: #e0f2fe;
	color: #0369a1;
	border: 1px solid #bae6fd;
	vertical-align: middle;
}

.run-status-badge {
	display: inline-block;
	padding: 4px 12px;
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	border-radius: 20px;
	letter-spacing: 0.5px;
}

.run-status-badge-small {
	display: inline-block;
	padding: 2px 8px;
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
	border-radius: 12px;
	letter-spacing: 0.3px;
}

/* Status Badge Colors */
.status-prepared {
	background-color: #fef3c7;
	color: #92400e;
	border: 1px solid #fbbf24;
}

.status-running {
	background-color: #dbeafe;
	color: #1d4ed8;
	border: 1px solid #3b82f6;
}

.status-completed {
	background-color: #d1fae5;
	color: #065f46;
	border: 1px solid #10b981;
}

.status-failed {
	background-color: #fee2e2;
	color: #991b1b;
	border: 1px solid #ef4444;
}

.status-cancelled {
	background-color: #f3f4f6;
	color: #4b5563;
	border: 1px solid #9ca3af;
}

.status-scheduled {
	background-color: #ede9fe;
	color: #6b21a8;
	border: 1px solid #8b5cf6;
}

.status-default {
	background-color: #f1f5f9;
	color: #64748b;
	border: 1px solid #cbd5e1;
}

/* Campaign Data Lists Card */
.data-lists-card .card-icon {
	background-color: #0ea5e9;
}

.dev-tools { border: 1px solid #e6e8ec; border-radius: 14px; background: #fff; overflow: visible; box-shadow: 0 8px 24px rgba(43, 48, 59, 0.05); }
.dev-tools__header { display: flex; justify-content: space-between; gap: 24px; padding: 20px 22px; border-bottom: 1px solid #f0f1f4; }
.dev-tools__eyebrow { margin-bottom: 5px; color: #8a909c; font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }
.dev-tools h3 { margin: 0 0 5px; color: #2b303b; font-size: 17px; font-weight: 700; }
.dev-tools p { max-width: 780px; margin: 0; color: #8a909c; font-size: 13px; line-height: 1.5; }
.dev-tools__badge { align-self: flex-start; padding: 3px 8px; border: 1px solid #f2c46d; border-radius: 6px; background: #fff8e8; color: #9a6510; font-size: 10px; font-weight: 800; letter-spacing: 0.08em; }
.dev-tools__body { display: grid; grid-template-columns: minmax(260px, 0.8fr) minmax(340px, 1.2fr) auto; gap: 18px; align-items: start; padding: 20px 22px; }
.dev-field { position: relative; }
.dev-field label { display: block; margin-bottom: 6px; color: #2b303b; font-size: 12px; font-weight: 700; }
.dev-field .form-control, .dev-field .form-select { min-height: 39px; border-color: #e6e8ec; border-radius: 8px; color: #2b303b; font-size: 13px; box-shadow: none; }
.dev-field .form-control:focus, .dev-field .form-select:focus { border-color: #2b6cff; box-shadow: 0 0 0 2px rgba(43, 108, 255, 0.1); }
.dev-field small, .dev-search-note { display: block; margin-top: 5px; color: #8a909c; font-size: 11px; }
.dev-municipality-results { position: absolute; z-index: 30; top: 67px; right: 0; left: 0; max-height: 260px; overflow-y: auto; padding: 5px; border: 1px solid #e6e8ec; border-radius: 10px; background: #fff; box-shadow: 0 12px 30px rgba(43, 48, 59, 0.14); }
.dev-municipality-results button { display: flex; flex-direction: column; gap: 2px; width: 100%; padding: 9px 10px; border: 0; border-radius: 7px; background: transparent; color: #2b303b; font-size: 12px; text-align: left; }
.dev-municipality-results button:hover { background: #f3f5f9; }
.dev-municipality-results span { color: #8a909c; font-size: 10px; }
.dev-selected { margin-top: 7px; color: #198754; font-size: 12px; font-weight: 600; }
.dev-selected span { margin-left: 5px; color: #8a909c; font-size: 10px; font-weight: 500; }
.dev-actions { display: flex; gap: 8px; padding-top: 23px; }
.dev-btn { min-height: 39px; padding: 0 13px; border: 1px solid #e6e8ec; border-radius: 8px; background: transparent; color: #2b303b; font-size: 12px; font-weight: 650; white-space: nowrap; }
.dev-btn:hover:not(:disabled) { background: #f3f5f9; border-color: #d6dae2; }
.dev-btn--run { background: #2b303b; border-color: #2b303b; color: #fff; }
.dev-btn--run:hover:not(:disabled) { background: #171a20; border-color: #171a20; }
.dev-btn:disabled { cursor: not-allowed; opacity: 0.5; }
.dev-result { grid-column: 1 / -1; padding: 11px 13px; border-radius: 9px; font-size: 12px; line-height: 1.6; }
.dev-result--ok { border: 1px solid #b8e1c8; background: #eef9f2; color: #17683a; }
.dev-result--error { border: 1px solid #f3c2c2; background: #fff1f1; color: #a72b2b; }

@media (max-width: 992px) {
	.dev-tools__body { grid-template-columns: 1fr; }
	.dev-actions { padding-top: 0; }
}
</style>
